import { plural } from '../../../nucleus/format';
import { readsOf, writesOf } from './items';
import {
    needWord, stuckWord, scheduleLine, frequencyWord, triggerKindWord, pluginStateWord, nextWord,
} from './words';

// THE LISTS' WORDS — what My plugins, the Plugin store and Automations say of
// each item in a line: a card's note, a row's cell, a failed save. Read from
// the shapes items.js makes; nothing here works a state out from flags.

// ── a plugin, in a line ─────────────────────────────────────────────────────

/** How a plugin stands, in a line: "Setting up · Next: Sign in to Google
 *  Calendar", "Ready", "Off", or (in the store) "Not set up". */
export function pluginNote(p) {
    if (!p?.state) return null;
    if (p.paused) return pluginStateWord('paused');
    if (!p.installed) return pluginStateWord('not_installed');
    if (p.state === 'ready') return pluginStateWord('ready');
    const need = (p.needs || []).find((n) => n.kind !== 'install');
    const next = need ? nextWord(needWord(need, p.lifecycle?.approval)) : null;
    return [pluginStateWord(p.state), next].filter(Boolean).join(' · ');
}

/** A stuck tool of a Ready plugin, said for the plugin: "Search pages can't run: …". */
export const stuckNote = (p) => (!p?.paused && (p?.troubled || []).length ? stuckWord(p.troubled[0]) : null);

/** Whether a plugin's line is a want (Setting up: the warn tone), or quiet. */
export function pluginTone(p) {
    if (!p?.installed || p.paused) return null;
    return p.settingUp ? 'wants' : null;
}

/** A plugin's tools as the store names them: [{ id, name }] (a plugin with none is one tool, itself). */
function toolNames(p) {
    const declared = (p?.plugin?.tools || []).filter((t) => t && t.tool_id).map((t) => ({ id: t.tool_id, name: t.name || t.tool_id }));
    if (declared.length) return declared;
    return (p?.tools || []).filter((t) => t && t.tool_id).map((t) => ({ id: t.tool_id, name: t.name || t.tool_id }));
}

/** A store card's note: how many tools, and the first one's name ("4 tools · Import messages"). */
export function storeNote(p) {
    const names = toolNames(p);
    const n = p?.toolsCount || names.length || 1;
    const count = plural(n, 'tool');
    return names.length ? `${count} · ${names[0].name}` : count;
}

/** What a plugin asks for, counted: { reads, writes, outside } (schemas read, kinds written, effects outside ofSelf). */
export function asksCounts(p) {
    const plugin = p?.plugin || {};
    const reads = readsOf(null, plugin).length;
    const decl = [
        ...(plugin.output_declaration || []),
        ...(plugin.tools || []).flatMap((t) => t?.output_declaration || []),
    ];
    const writes = new Set(writesOf(decl).map((w) => `${w.verb}:${w.what}`)).size;
    const declaredEffects = (plugin.tools || []).reduce((n, t) => n + (t?.effects || []).length, 0) + (plugin.effects || []).length;
    const outside = p?.effects ? p.effects.count : declaredEffects;
    return { reads, writes, outside };
}

/** "reads 2 · writes 1 · outside ofSelf 1", or "Nothing". */
export function asksLine(p) {
    const { reads, writes, outside } = asksCounts(p);
    const parts = [reads && `reads ${reads}`, writes && `writes ${writes}`, outside && `outside ofSelf ${outside}`].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Nothing';
}

/** Runs waiting for you, counted per plugin: { <pluginId>: n }. */
export function waitingByPlugin(requests) {
    const out = {};
    (requests || []).forEach((r) => {
        if (!r?.pluginId || (r.status && r.status !== 'pending')) return;
        out[r.pluginId] = (out[r.pluginId] || 0) + 1;
    });
    return out;
}

// ── an automation, in a line ────────────────────────────────────────────────

/** The events a change can start an automation on, in words. */
export const EVENTS = [
    { value: 'node.created', label: 'A node is created', of: 'is created' },
    { value: 'node.updated', label: 'A node changes', of: 'changes' },
    { value: 'node.deleted', label: 'A node is deleted', of: 'is deleted' },
    { value: 'file.uploaded', label: 'A file is uploaded', of: null },
];
const FINISHED = 'automation.completed';

/** "A note is created", "A node changes", "When Import games now finishes". */
export function eventWord(event, { schemaName = null, fromName = null } = {}) {
    if (event === FINISHED) return fromName ? `When ${fromName} finishes` : 'When the step before it finishes';
    const e = EVENTS.find((x) => x.value === event);
    if (!e) return triggerKindWord('event');
    if (schemaName && e.of) return `A ${schemaName} ${e.of}`;
    return e.label;
}

/** When it runs, in words: how often it runs automatically, its change, or when you run it. */
export function whenWord(a, ctx = {}) {
    if (a.trigger === 'schedule') return a.cron ? frequencyWord(a.cron) : triggerKindWord('schedule');
    if (a.trigger === 'event') return eventWord(a.event, ctx);
    return triggerKindWord('manual');
}

/** Whether an automation is a tool's own "runs automatically" (made on the tool, opened there). */
export const isToolSchedule = (a) => !!a?.toolSchedule || a?.status?.of === 'schedule';

/** An automation's state, one way: { state, word, tone, why } from its status. */
export const standingOf = (a) => a?.status || { state: 'off', word: 'Off', tone: 'quiet', why: null };

/** An automation's card note: a tool's "runs automatically" line ("Every hour", "Can't run: plugin
 *  not turned on"), else its state and trigger. */
export function automationNote(a) {
    const s = standingOf(a);
    if (isToolSchedule(a)) {
        return scheduleLine({ ...(a.schedule || {}), state: s.state, cron: a.cron });
    }
    if (s.state === 'off') return s.word;
    if (s.state === 'failing') return s.word;
    return triggerKindWord(a.trigger);
}

/** The sets of the Automations page, in the order they want a hand: Keeps failing · Can't run · On · Off. */
export const AUTOMATION_SETS = [
    { id: 'failing', title: 'Keeps failing', states: ['failing'] },
    { id: 'blocked', title: 'Can’t run', states: ['paused', 'blocked', 'needs_realm'] },
    { id: 'on', title: 'On', states: ['on', 'running'] },
    { id: 'off', title: 'Off', states: ['off'] },
];
/** The State facet's values and words, the sets'. */
export const AUTOMATION_FACET = AUTOMATION_SETS.map((s) => ({ id: s.id, label: s.title }));
export const setOf = (a) => (AUTOMATION_SETS.find((s) => s.states.includes(standingOf(a).state)) || AUTOMATION_SETS[3]).id;

// WHY AN AUTOMATION COULD NOT BE SAVED, TURNED ON OR RUN: the server's codes,
// said plainly; any other refusal in the server's own words.
const CODE_WORD = {
    TOOL_SCHEDULE_OWNED: 'Its owner already set this tool to run automatically. Change that on the tool.',
    REALM_NOT_GRANTED: 'The app that made it can’t use that realm. Choose one of the realms it was given.',
    REALM_REQUIRED: 'Choose a realm it runs in first.',
    PRIVACY_REALM_REQUIRED: 'Choose a realm it runs in first.',
    TOOL_ID_REQUIRED: 'Choose which tool it runs.',
    TOOL_NOT_FOUND: 'That tool isn’t in the plugin any more.',
    MIGRATED: 'This now runs automatically on its tool, which is where it runs from. Change it there.',
    PLUGIN_NOT_ALLOWED: 'The app that made it may not use this tool.',
    REMOVE_SOURCE_NOT_ALLOWED: 'It deletes what it reads, and the app that made it may not delete.',
    PLUGIN_PAUSED: 'Its plugin is off. Turn the plugin on first.',
    NOT_INSTALLED: 'Its plugin isn’t set up here.',
    NOT_CONFIGURED: 'A setting its plugin needs has no value.',
    CONSENT_REQUIRED: 'Its plugin isn’t turned on yet.',
};
/** A failed automation call, said for a person. */
export function automationError(e, fallback = 'That did not work.') {
    if (e?.code && CODE_WORD[e.code]) return CODE_WORD[e.code];
    return e?.message || fallback;
}
/** The same, as an Error a toast can say. */
export function plainError(e, fallback) {
    const out = new Error(automationError(e, fallback));
    out.code = e?.code;
    out.status = e?.status;
    return out;
}

/** A schema as a picker lists it ("Note"), and as a sentence says it ("note"). */
export const schemaLabel = (s) => s?.schema_metadata?.display_name || s?.name || s?.schema_name || null;
export const schemaWord = (s) => {
    const w = s?.schema_name || s?.name || s?.schema_metadata?.display_name || null;
    return w ? String(w).replace(/[_-]+/g, ' ').toLowerCase() : null;
};

// ── a flow's chain ──────────────────────────────────────────────────────────
// A flow runs its steps in order because each step after the first is started
// by the one before it finishing: trigger_type 'event', trigger_config
// { event: 'automation.completed', from: <the step before> } (the runner's
// _event_sub_matches). Saving a flow writes that trigger on every later step
// that does not have it yet; the first step keeps its own trigger.

const FINISH = 'automation.completed';
const fromsOf = (tc) => {
    const f = tc?.from;
    return (Array.isArray(f) ? f : [f]).filter(Boolean).map(String);
};
/** Whether an automation starts when another finishes (and which: its `from` ids). */
export const finishesFrom = (a) => (a?.trigger === 'event' && [a.triggerConfig?.event, a.triggerConfig?.on].includes(FINISH)
    ? fromsOf(a.triggerConfig) : null);

/** What starts an automation, in words that follow "runs …":
 *  "when Import games now finishes", "when a note is created", "when you run it", "every hour". */
export function startsPhrase(a, nameOf = () => null, schemaOf = () => null) {
    const lower = (w) => (w ? `${w[0].toLowerCase()}${w.slice(1)}` : w);
    const from = finishesFrom(a);
    if (from) {
        const names = from.map((id) => nameOf(id) || 'another automation');
        return `when ${listWordsOf(names)} ${names.length > 1 ? 'finish' : 'finishes'}`;
    }
    if (a?.trigger === 'event') return `when ${lower(eventWord(a.event, { schemaName: a.schemaId ? schemaOf(a.schemaId) : null }))}`;
    if (a?.trigger === 'schedule') return a.cron ? lower(frequencyWord(a.cron)) : 'automatically';
    return 'when you run it';
}
const listWordsOf = (xs) => (xs.length < 2 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** Why an automation cannot stand at step `i` of a flow, or null. A tool that
 *  runs automatically belongs to its tool: it can only start a flow. */
// the runner starts at most this many steps one after another (automation_tasks MAX_DEPTH, counted from the first)
export const FLOW_MOST = 6;
export function cannotStandAt(a, i) {
    if (i > 0 && isToolSchedule(a)) return `${a.name} runs its tool automatically, so it can only be the first step.`;
    if (i >= FLOW_MOST) return `A flow runs at most ${FLOW_MOST} steps one after another, so ${a.name} would never start.`;
    return null;
}

/** The chain a flow's steps need: for each step after the first, whether its
 *  trigger must change, and the words that say so.
 *  → [{ id, name, prevId, prevName, change, was, line, blocked }] */
export function chainPlan(stepIds, byId, schemaOf = () => null) {
    const nameOf = (id) => byId(id)?.name || null;
    return (stepIds || []).map((id, i) => {
        const a = byId(id);
        const name = a?.name || 'An automation';
        const blocked = a ? cannotStandAt(a, i) : null;
        if (!a) return { id, name, prevId: null, prevName: null, change: false, was: null, line: null, blocked };
        if (i === 0) {
            // the first step keeps its own trigger, unless that is another step
            // of this flow finishing: that would run the flow round in a loop
            const loops = (finishesFrom(a) || []).some((f) => (stepIds || []).map(String).includes(f));
            const was = loops ? startsPhrase(a, nameOf, schemaOf) : null;
            return {
                id, name, prevId: null, prevName: null, change: loops, was, blocked, manual: loops,
                line: loops ? `${name} now runs when you run it, instead of ${was}.` : null,
            };
        }
        const prevId = stepIds[i - 1];
        const prevName = nameOf(prevId) || 'the step before';
        const from = finishesFrom(a);
        const chained = !!from && from.length === 1 && from[0] === String(prevId);
        const was = chained ? null : startsPhrase(a, nameOf, schemaOf);
        return {
            id, name, prevId, prevName, change: !chained && !blocked, was, blocked,
            line: chained || blocked ? null : `${name} now runs when ${prevName} finishes, instead of ${was}.`,
        };
    });
}

/** The PUT body that chains `a` to the step before it (or, with no step
 *  before, makes it start only when run): its own trigger keys go, the rest stays. */
export function chainBody(a, prevId) {
    const keep = Object.fromEntries(Object.entries(a?.triggerConfig || {})
        .filter(([k]) => !['cron', 'timezone', 'event', 'on', 'from', 'schema_id', 'join_mode'].includes(k)));
    if (prevId == null) return { trigger_type: 'manual', trigger_config: keep };
    return { trigger_type: 'event', trigger_config: { ...keep, event: FINISH, from: String(prevId) } };
}
